/**
 * WindZone2D Component
 *
 * Applies constant directional force to bodies within the zone (wind, fans, air vents).
 * Can be directional (constant direction) or radial (outward/inward from center).
 */

import {
  component,
  displayName,
  icon,
  color,
  category,
  allowMultiple,
  requireComponent,
  field,
  customEditor,
} from "../attributes";

/**
 * Wind zone mode:
 * - 0: Directional - constant direction wind
 * - 1: Radial - outward/inward from center
 */
export const enum WindZoneMode {
  Directional = 0,
  Radial = 1,
}

@component("core.WindZone2D")
@customEditor("WindZone2DEditor")
@displayName("Wind Zone 2D")
@icon("Wind")
@color("#1abc9c")
@category("Physics 2D")
@allowMultiple(false)
@requireComponent(["core.Transform"])
export class WindZone2D {
  /** Wind mode (0=Directional, 1=Radial) */
  @field({ type: "i32", label: "Mode" })
  mode: number = WindZoneMode.Directional;

  /** Wind direction (for Directional mode) */
  @field({ type: "vec2", label: "Direction" })
  direction: { x: number; y: number } = { x: 1, y: 0 };

  /** Wind strength - force magnitude */
  @field({ type: "f32", label: "Strength", step: 0.5 })
  strength: number = 10.0;

  /** Turbulence - random variation in force (0 = steady wind) */
  @field({ type: "f32", label: "Turbulence", min: 0, step: 0.1 })
  turbulence: number = 0;

  /** Turbulence frequency - how fast turbulence changes */
  @field({ type: "f32", label: "Turbulence Freq", min: 0.1, step: 0.1 })
  turbulenceFrequency: number = 1.0;

  /** Pulse - oscillating strength (0 = no pulse) */
  @field({ type: "f32", label: "Pulse", min: 0, step: 0.1 })
  pulse: number = 0;

  /** Pulse frequency - oscillation speed (Hz) */
  @field({ type: "f32", label: "Pulse Freq", min: 0.1, step: 0.1 })
  pulseFrequency: number = 1.0;

  // ─── Zone Shape ────────────────────────────────────────────────────────────

  /** Zone shape (0=Circle, 1=Box) */
  @field({ type: "i32", label: "Shape" })
  shape: number = 1; // Box by default for wind corridors

  /** Zone radius (for Circle shape) */
  @field({ type: "f32", label: "Radius", min: 0.1, step: 0.1 })
  radius: number = 5;

  /** Zone width (for Box shape) */
  @field({ type: "f32", label: "Width", min: 0.1, step: 0.1 })
  width: number = 8;

  /** Zone height (for Box shape) */
  @field({ type: "f32", label: "Height", min: 0.1, step: 0.1 })
  height: number = 4;

  // ─── Falloff ───────────────────────────────────────────────────────────────

  /** Falloff type (0=None, 1=Linear, 2=Smoothstep) */
  @field({ type: "i32", label: "Falloff" })
  falloff: number = 0;

  /** Falloff start distance (full strength within this) */
  @field({ type: "f32", label: "Falloff Start", min: 0, step: 0.1 })
  falloffStart: number = 0;

  /** Priority for overlapping zones */
  @field({ type: "i32", label: "Priority", min: 0, max: 100 })
  priority: number = 0;

  /** Which physics layers are affected */
  @field({ type: "i32", label: "Affected Layers", hidden: true })
  affectedLayers: number = 0xFFFF;
}
